import { Tarefa } from "../services/tarefas";

export type Urgencia = "concluida" | "vencida" | "critica" | "alta" | "media" | "baixa";

export interface UrgenciaInfo {
  nivel: Urgencia;
  // Tailwind classes
  bg: string;       // background da borda lateral
  ring: string;     // anel/borda
  badge: string;    // chip
  texto: string;    // label legível
}

/**
 * Calcula urgência por proximidade do prazo. "Vencida" compara com o prazo
 * ATUAL — se o gestor adiou o prazo, a tarefa volta a ficar no verde/amarelo.
 * O `prazoOriginal` continua existindo no banco só pra métricas de atraso
 * histórico (quanto a tarefa foi postergada), não pra decidir o badge.
 */
export function urgenciaTarefa(t: Tarefa, agora = new Date()): UrgenciaInfo {
  if (t.status === "concluido") {
    return {
      nivel: "concluida",
      bg: "bg-slate-300",
      ring: "ring-slate-200",
      badge: "bg-slate-100 text-slate-500",
      texto: "Concluída",
    };
  }

  // Card na coluna "Aguardando cliente" NÃO vence — o atraso não é da equipe
  // (pedido do Adriel, jul/2026). Espelha STATUS_TAREFA_NAO_VENCE do backend.
  if (t.status === "aguardando_cliente") {
    return {
      nivel: "baixa",
      bg: "bg-purple-400",
      ring: "ring-purple-200",
      badge: "bg-purple-100 text-purple-700",
      texto: "Aguardando cliente",
    };
  }

  const prazo = new Date(t.prazo);
  if (agora > prazo) {
    return {
      nivel: "vencida",
      bg: "bg-red-500",
      ring: "ring-red-300",
      badge: "bg-red-100 text-red-700",
      texto: "Vencida",
    };
  }

  const totalMs = prazo.getTime() - new Date(t.criadoEm).getTime();
  const restanteMs = prazo.getTime() - agora.getTime();
  const pctRestante = totalMs <= 0 ? 0 : restanteMs / totalMs;

  if (pctRestante <= 0.2) {
    return {
      nivel: "critica",
      bg: "bg-orange-500",
      ring: "ring-orange-300",
      badge: "bg-orange-100 text-orange-700",
      texto: "Crítica",
    };
  }
  if (pctRestante <= 0.5) {
    return {
      nivel: "alta",
      bg: "bg-amber-400",
      ring: "ring-amber-200",
      badge: "bg-amber-100 text-amber-700",
      texto: "Atenção",
    };
  }
  return {
    nivel: "baixa",
    bg: "bg-emerald-500",
    ring: "ring-emerald-200",
    badge: "bg-emerald-100 text-emerald-700",
    texto: "No prazo",
  };
}

// Rótulos de fallback. As colunas são editáveis (ColunaKanban) — estes valores
// cobrem o layout padrão; quando o status não estiver aqui, usa-se a própria chave.
export const STATUS_LABELS: Record<string, string> = {
  demandas: "Demandas",
  a_fazer: "A fazer",
  em_andamento: "Em andamento",
  em_revisao: "Em revisão",
  ajustes: "Ajustes",
  aguardando_aprovacao: "Aprovação interna",
  enviar_cliente: "Enviar ao cliente",
  aguardando_cliente: "Aguardando cliente",
  aguardando: "Aguardando",
  veicular: "Veicular",
  concluido: "Finalizados",
};

export function diasRestantes(prazoIso: string, agora = new Date()): number {
  const prazo = new Date(prazoIso);
  return Math.ceil((prazo.getTime() - agora.getTime()) / (24 * 60 * 60 * 1000));
}

/**
 * Formata o prazo de uma tarefa pra exibição. Quando `prazoComHora=true`,
 * inclui hora ("DD/MM/YYYY 14:30"); quando false, só a data.
 */
export function formatarPrazo(t: { prazo: string; prazoComHora?: boolean }): string {
  const d = new Date(t.prazo);
  if (isNaN(d.getTime())) return "";
  const data = d.toLocaleDateString("pt-BR");
  if (!t.prazoComHora) return data;
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${data} ${h}:${m}`;
}
